// İş ortağı (merchant) firma profilinin ne kadarının tamamlandığını hesaplar.
// Sidebar'daki "Profilini Tamamla" kutusu müşteride getCustomerProfileCompletion'ı,
// merchant'ta bu fonksiyonu kullanır. Kaynaklar: firma profili (warehouse_companies),
// firma imzası/kaşesi/imza yetkilisi, ZORUNLU evraklar, iş birliği sözleşmesi, banka bilgisi.
//
// Her maddenin target'ı Şirket Bilgilerim ekranının sekme anahtarıdır
// (profile | documents | contracts | bankInformations) — tıklanınca o sekme açılır.

const filled = (value) => String(value ?? "").trim().length > 0;

// Firma profilinde doldurulması beklenen alanlar (ProfileTab'de "required" olanlar + kimlik/marka).
const COMPANY_FIELDS = [
  { key: "title", label: "Firma Adı" },
  { key: "registrationTitle", label: "Firma Resmi Adı" },
  { key: "taxAdministration", label: "Vergi Dairesi" },
  { key: "taxNo", label: "Vergi Numarası" },
  { key: "address", label: "Açık Adres" },
  { key: "phone", label: "Telefon" },
  { key: "email", label: "E-posta" },
  { key: "logo", label: "Firma Logosu" },
];

// Evrak/sözleşme kaydı "yüklenmiş" sayılır mı? 0 = reddedildi, 2 = yeniden yükleme bekleniyor.
const REUPLOAD_REQUIRED_STATUSES = new Set(["0", "2"]);

function latestByType(records) {
  if (!Array.isArray(records)) return {};
  const latest = {};
  records.forEach((record) => {
    const type = String(record?.type ?? "").trim();
    if (!type) return;
    const prev = latest[type];
    const time = Number(record?.createdDateTime ?? record?.created_date_time) || 0;
    const prevTime = Number(prev?.createdDateTime ?? prev?.created_date_time) || 0;
    if (!prev || time > prevTime) latest[type] = record;
  });
  return latest;
}

function isUploaded(record) {
  if (!record || typeof record !== "object") return false;
  const status = String(record?.status ?? record?.statusNumber ?? "").trim();
  return !REUPLOAD_REQUIRED_STATUSES.has(status);
}

// userInfo: GetUserInfos sonucu — KİŞİSEL profil maddeleri (müşteriyle aynı mantık)
// consents: GetUserConsents sonucundaki "consents" dizisi — zorunlu onaylar (KVKK vb.)
// company: GetWarehouseCompany sonucu (null ise firma maddeleri puana KATILMAZ)
// signature: GetCompanySignature sonucu { fileURL, stampURL, signatureAuthorityName, ... }
// documents / agreements: firma evrak & sözleşme kayıtları (null ise puana KATILMAZ)
// documentTypes / agreementTypes: [{ type, label, isMandatory }] — zorunlu olanlar puana girer
// bankAccounts: banka hesapları listesi (null ise puana katılmaz)
//
// page alanı, eksik maddeye tıklanınca gidilecek EKRANI söyler:
//   "profile" → Profil sayfası (target: basic | permissions)
//   "firm"    → Şirket Bilgilerim (target: profile | documents | contracts | bankInformations)
// { percent, completed, total, missing: [{ label, target, page }] }
export function getMerchantProfileCompletion({
  userInfo = null,
  consents = [],
  company = null,
  signature = null,
  documents = null,
  agreements = null,
  documentTypes = null,
  agreementTypes = null,
  bankAccounts = null,
  // Firma evrak/sözleşme kilidi (isContractLocked). Kilitliyken firma profilinde YALNIZ
  // "Evraklarım" ve "Sözleşmelerim" sekmeleri çiziliyor (EditWareHouseFirmModal), yani
  // "profile" ve "bankInformations" hedefli kalemler giderilemez → listeye HİÇ girmez
  // (yüzde de bunlara göre hesaplanmaz; aksi halde kullanıcı asla %100'e ulaşamaz gibi görünür).
  firmLocked = false,
} = {}) {
  if (!userInfo && !company) return null;

  const items = [];

  // --- Kişisel profil (müşteri kutusuyla aynı maddeler) ---
  if (userInfo) {
    items.push(
      { label: "Ad Soyad", done: filled(userInfo?.name) && filled(userInfo?.lastname), target: "basic", page: "profile" },
      { label: "E-posta", done: filled(userInfo?.email), target: "basic", page: "profile" },
      { label: "Telefon", done: filled(userInfo?.phone), target: "basic", page: "profile" },
      { label: "Profil Fotoğrafı", done: filled(userInfo?.avatar), target: "basic", page: "profile" },
    );
  }

  // Zorunlu onaylar (KVKK, Kullanıcı Sözleşmesi …) — müşteride olduğu gibi eksik sayılır.
  (Array.isArray(consents) ? consents : [])
    .filter((consent) => consent?.required)
    .forEach((consent) => {
      items.push({
        label: consent?.title || "Onay",
        done: Boolean(consent?.accepted),
        target: "permissions",
        page: "profile",
      });
    });

  // --- Firma profili ---
  // NOT: aşağıdaki üç blok "profile" / "bankInformations" sekmelerine gider. Firma kilitliyken
  // o sekmeler hiç çizilmediği için kalemler de listelenmez (bkz. firmLocked).
  if (company && !firmLocked) {
    COMPANY_FIELDS.forEach((field) => {
      items.push({ label: field.label, done: filled(company?.[field.key]), target: "profile", page: "firm" });
    });
  }

  if (signature && !firmLocked) {
    items.push({
      label: "İmza Yetkilisi",
      done: filled(signature?.signatureAuthorityName),
      target: "profile",
      page: "firm",
    });
    items.push({ label: "Firma İmzası", done: filled(signature?.fileURL), target: "profile", page: "firm" });
    // Kaşe otomatik üretilir; "tamam" sayılması için firmanın ONAYLAMASI (stampStatus=2) gerekir.
    items.push({ label: "Firma Kaşesi", done: Number(signature?.stampStatus ?? 0) === 2, target: "profile", page: "firm" });
  }

  if (Array.isArray(bankAccounts) && !firmLocked) {
    items.push({ label: "Banka Bilgileri", done: bankAccounts.length > 0, target: "bankInformations", page: "firm" });
  }

  // Evraklar: yalnız ZORUNLU tipler puana girer (AuthContext/companyCompliance ile aynı kural).
  if (Array.isArray(documents) && Array.isArray(documentTypes)) {
    const latest = latestByType(documents);
    documentTypes
      .filter((doc) => doc?.isMandatory && doc?.type)
      .forEach((doc) => {
        items.push({
          label: doc.label || doc.type,
          done: isUploaded(latest[doc.type]),
          target: "documents",
          page: "firm",
        });
      });
  }

  if (Array.isArray(agreements) && Array.isArray(agreementTypes)) {
    const latest = latestByType(agreements);
    agreementTypes
      .filter((agreement) => agreement?.type)
      .forEach((agreement) => {
        // Sözleşme kaydı tek tip olduğunda tipsiz kayıtlar da o tipe sayılır. Yedekte
        // dizinin İLK elemanı değil, EN GÜNCEL kayıt alınır (companyCompliance.getLatestByType
        // ile aynı kural); dizi sırası güncellik garantisi vermiyor.
        const untypedFallback =
          agreementTypes.length === 1 && Array.isArray(agreements)
            ? agreements
                .filter((record) => !String(record?.type ?? "").trim())
                .slice()
                .sort(
                  (a, b) => Number(b?.createdDateTime || 0) - Number(a?.createdDateTime || 0)
                )[0] || null
            : null;
        const record = latest[agreement.type] || untypedFallback;
        items.push({
          label: agreement.label || "İş Birliği Sözleşmesi",
          done: isUploaded(record),
          target: "contracts",
          page: "firm",
        });
      });
  }

  const total = items.length;
  const completed = items.filter((item) => item.done).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 100;

  return {
    percent,
    completed,
    total,
    missing: items.filter((item) => !item.done).map(({ label, target, page }) => ({ label, target, page })),
  };
}
